import React, { useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/hooks';
import { LoadingSpinner } from '@/components';
import LandingPage from '@/pages/HomePage';
import { ErrorPage } from '@/pages/ErrorPage';
import { isDev } from './constants';
import { isMobilePhone } from '@/mobile/detection/deviceDetection';
import { SpaceAccessGuard } from '@/components/SpaceAccessGuard';

// Lazy load mobile components for code splitting
const MobileHome = React.lazy(() =>
  import('@/mobile/MobileHome').then((module) => ({ default: module.MobileHome })),
);
const MobileStudio = React.lazy(() =>
  import('@/mobile/MobileStudio').then((module) => ({ default: module.MobileStudio })),
);

const KNOWN_PATHS = ['/studio', '/devices', '/live', '/settings'];

/**
 * MainPage — single desktop entry point.
 *
 * Mobile phone redirect logic (previously in MobileRouteGuard and
 * MobileHomeRedirect, which are now unused) is co-located here.
 * This allows ALL desktop routes to share a single <Route>, preventing
 * React Router from unmounting/remounting the entire component tree when
 * switching between navigation tabs.
 */
function MainPage() {
  const { isAuthenticated, isLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = isMobilePhone();

  useEffect(() => {
    if (!isPhone) return;
    if (location.pathname === '/' || location.pathname === '/studio') {
      navigate(`/mobile/studio${location.search}${location.hash}`, { replace: true });
    }
  }, [isPhone, location.pathname, location.search, location.hash, navigate]);

  if (isPhone && (location.pathname === '/' || location.pathname === '/studio')) {
    return <LoadingSpinner />;
  }

  // The desktop <Route path="/*"> catches all paths not matched by the
  // mobile routes above. Unknown paths render a 404 below.
  const isKnownPath =
    location.pathname === '/' ||
    KNOWN_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p + '/')) ||
    (isDev && (location.pathname === '/demo' || location.pathname.startsWith('/demo/')));
  if (!isKnownPath) {
    return (
      <ErrorPage error={{ message: 'Page not found', name: '404' }} resetErrorBoundary={() => {}} />
    );
  }

  if (isLoading || !isAuthenticated) {
    return <LoadingSpinner />;
  }
  return (
    <SpaceAccessGuard>
      <LandingPage />
    </SpaceAccessGuard>
  );
}

/**
 * MobilePage - Wrapper for mobile routes that handles authentication
 * This mirrors MainPage but for mobile routes, ensuring useUser() is called
 * to populate the UserContext with isSuperUser and other auth data.
 */
function MobilePage({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useUser();
  if (isLoading || !isAuthenticated) {
    return <LoadingSpinner />;
  }
  return <SpaceAccessGuard>{children}</SpaceAccessGuard>;
}

function Routing() {
  return (
    <Routes>
      {/* Mobile routes - placed first for priority */}
      <Route
        path="/mobile"
        element={
          <MobilePage>
            <React.Suspense fallback={<LoadingSpinner />}>
              <MobileHome />
            </React.Suspense>
          </MobilePage>
        }
      />
      <Route
        path="/mobile/studio"
        element={
          <MobilePage>
            <React.Suspense fallback={<LoadingSpinner />}>
              <MobileStudio />
            </React.Suspense>
          </MobilePage>
        }
      />

      {/* Catch-all desktop route. See MainPage JSDoc for why the component
          tree is shared across /studio, /live, /devices, /settings, etc. */}
      <Route path="/*" element={<MainPage />} />
    </Routes>
  );
}

export default Routing;
